import { Phone, MapPin, Mail, Send } from "lucide-react";
import { useState, useEffect } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [loadCaptcha, setLoadCaptcha] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const section = document.getElementById("contact");

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadCaptcha(true);
        }
      },
      {
        rootMargin: "200px",
      }
    );

    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(false);
    setErrors({});
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const topic = String(formData.get("topic") || "").trim();
    const message = String(formData.get("message") || "").trim();
    const company = String(formData.get("company") || "");

    const newErrors: Record<string, string> = {};

    if (!name) newErrors.name = "יש למלא שם מלא.";
    if (!email) newErrors.email = "יש למלא אימייל.";
    if (!phone) newErrors.phone = "יש למלא מספר טלפון.";
    if (!message) newErrors.message = "יש למלא הודעה.";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      newErrors.email = "כתובת אימייל אינה תקינה.";
    }

    const phoneRegex = /^0\d{8,9}$/;
    if (phone && !phoneRegex.test(phone)) {
      newErrors.phone = "מספר טלפון אינו תקין.";
    }

    if (company) {
      setLoading(false);
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrorField = Object.keys(newErrors)[0];
      document.getElementById(firstErrorField)?.focus();
      setLoading(false);
      return;
    }

    const data = { name, email, phone, topic, message, captchaToken };

    if (!captchaToken) {
      setErrors({ general: "יש לאשר שאינך רובוט." });
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(import.meta.env.VITE_CONTACT_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      console.log("Response status:", res.status);
      const json = await res.json().catch(() => null);
      console.log("Response body:", json);
      if (!res.ok) throw new Error("Failed");

      setErrors({});
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setErrors({});
      }, 6000);
      e.currentTarget.reset();
      setCaptchaToken(null);
    } catch {
      setErrors({ general: "אירעה שגיאה בשליחה. נסו שוב." });
    }
    setLoading(false);
  };

  return (
    <section
      id="contact"
      aria-labelledby="contact-title"
      className="py-32 md:py-40 bg-gradient-to-b from-slate-950 via-black to-black relative border-t border-white/10 overflow-hidden sm:scroll-mt-8"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(59,130,246,0.12),transparent_60%)] pointer-events-none"
      />
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-slate-900 to-slate-950 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/10 flex flex-col md:flex-row">
          {/* Info Panel */}
          <aside
            aria-labelledby="contact-info-title"
            className="w-full md:w-2/5 p-10 md:p-12 flex flex-col border-l border-white/10"
          >
            <div>
              <h3
                id="contact-info-title"
                className="text-3xl font-bold mb-6 text-white"
              >
                יצירת קשר
              </h3>

              <p className="text-slate-300 font-light mb-10 leading-relaxed">
                כאן בשבילכם. מוזמנים ליצור קשר לתיאום פגישת ייעוץ ראשונית ללא
                התחייבות.
              </p>

              <address aria-label="פרטי יצירת קשר" className="not-italic">
                <ul className="space-y-6">
                  <li>
                    <ContactItem
                      icon={Phone}
                      text="03-5563036"
                      href="tel:035563036"
                      label="טלפון"
                    />
                  </li>
                  <li>
                    <ContactItem
                      icon={Mail}
                      text="office@ezlawyer.co.il"
                      href="mailto:office@ezlawyer.co.il"
                      label="אימייל"
                    />
                  </li>
                  <li>
                    <ContactItem
                      icon={MapPin}
                      text="מוטה גור 7 בניין C קומה חמישית, פתח תקווה"
                      href="https://maps.app.goo.gl/U6x1H2jgnRSV9o3K8"
                      label="כתובת"
                    />
                  </li>
                </ul>
                <p className="text-sm text-slate-400 mr-11">* חניה חינם.</p>
              </address>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-sm text-slate-400">
                * המידע באתר אינו מהווה ייעוץ משפטי מחייב.
              </p>
            </div>
          </aside>

          {/* Form Panel */}
          <div className="w-full md:w-3/5 p-10 md:p-12">
            <header className="mb-8">
              <h2 id="contact-title" className="text-2xl font-bold text-white">
                בואו נדבר על הסיפור שלכם
              </h2>

              <p className="mt-3 text-slate-300 font-light leading-relaxed">
                השאירו פרטים ונחזור אליכם לשיחת ייעוץ ראשונית, אישית ודיסקרטית –
                כדי להבין יחד איך נכון לפעול.
              </p>
            </header>

            {submitted && !loading && (
              <div className="mb-6 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-5 py-4 text-emerald-200 text-sm md:text-base">
                תודה! ההודעה נשלחה בהצלחה. נחזור אליכם בהקדם.
              </div>
            )}

            {/* Live region */}
            <div aria-live="polite" role="status" className="sr-only">
              {submitted && "הטופס נשלח בהצלחה"}
              {errors.general && errors.general}
            </div>

            <form
              className="space-y-6"
              aria-labelledby="contact-title"
              aria-describedby="form-desc"
              onSubmit={onSubmit}
              noValidate
              aria-busy={loading}
            >
              <p id="form-desc" className="sr-only">
                טופס יצירת קשר. כל השדות חובה.
              </p>

              <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <legend className="sr-only">פרטי יצירת קשר</legend>

                <input
                  type="text"
                  name="company"
                  autoComplete="off"
                  tabIndex={-1}
                  className="hidden"
                />

                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-slate-300 mb-2"
                  >
                    שם מלא
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "error-name" : undefined}
                    autoComplete="name"
                    className="w-full px-4 py-3 bg-[#1f2937] border border-white/10 text-slate-200 placeholder-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] focus:border-blue-500 transition-all"
                  />
                  {errors.name && (
                    <p
                      id="error-name"
                      role="alert"
                      className="text-red-400 text-sm mt-2"
                    >
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-300 mb-2"
                  >
                    אימייל
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "error-email" : undefined}
                    autoComplete="email"
                    className="w-full px-4 py-3 bg-[#1f2937] border border-white/10 text-slate-200 placeholder-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] focus:border-blue-500 transition-all"
                  />
                  {errors.email && (
                    <p
                      id="error-email"
                      role="alert"
                      className="text-red-400 text-sm mt-2"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-slate-300 mb-2"
                  >
                    טלפון
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "error-phone" : undefined}
                    autoComplete="tel"
                    inputMode="tel"
                    className="w-full px-4 py-3 bg-[#1f2937] border border-white/10 text-slate-200 placeholder-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] focus:border-blue-500 transition-all"
                  />
                  {errors.phone && (
                    <p
                      id="error-phone"
                      role="alert"
                      className="text-red-400 text-sm mt-2"
                    >
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Topic (optional) */}
                <div>
                  <label
                    htmlFor="topic"
                    className="block text-sm font-medium text-slate-300 mb-2"
                  >
                    באיזה תחום נוכל לעזור?{" "}
                    <span className="text-slate-400">(אופציונלי)</span>
                  </label>

                  <select
                    id="topic"
                    name="topic"
                    defaultValue=""
                    className="w-full px-4 py-3 bg-[#1f2937] border border-white/10 text-slate-200 rounded-xl
               focus:outline-none focus:ring-2 focus:ring-blue-500/30
               focus:border-blue-500 transition-all"
                  >
                    <option value="">בחרו נושא…</option>
                    <option value="תאונת דרכים">תאונת דרכים</option>
                    <option value="רשלנות רפואית">רשלנות רפואית</option>
                    <option value="ביטוח לאומי">ביטוח לאומי</option>
                    <option value="אובדן כושר עבודה">אובדן כושר עבודה</option>
                    <option value="אחר">אחר</option>
                  </select>
                </div>
              </fieldset>
              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  כיצד אוכל לעזור?
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  aria-required="true"
                  aria-invalid={!!errors.message}
                  aria-describedby={
                    errors.message ? "error-message" : undefined
                  }
                  className="w-full px-4 py-3 bg-[#1f2937] border border-white/10 text-slate-200 placeholder-slate-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] focus:border-blue-500 transition-all resize-none"
                />
                {errors.message && (
                  <p
                    id="error-message"
                    role="alert"
                    className="text-red-400 text-sm mt-2"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              {errors.general && !submitted && !loading && (
                <p role="alert" className="text-red-400 text-sm">
                  {errors.general}
                </p>
              )}

              {/* Turnstile */}
              <div className="flex justify-start pt-4">
                {mounted && loadCaptcha && (
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setCaptchaToken(token)}
                  />
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitted || loading || !captchaToken}
                className="w-full md:w-auto px-8 py-4 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-500 transition-all shadow-lg hover:shadow-blue-500/30 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <span>
                  {loading
                    ? "שולח..."
                    : submitted
                    ? "נשלח ✓"
                    : "לתיאום שיחה עם עו״ד עזרא"}
                </span>
                <Send size={18} aria-hidden="true" />
              </button>

              {/* Turnstile notice */}
              <p className="text-xs text-slate-400 mt-3 text-center">
                טופס זה מוגן באמצעות Cloudflare Turnstile למניעת ספאם.
              </p>

              {/* Privacy note */}
              <p className="text-sm text-slate-400 leading-relaxed">
                סודיות מלאה מובטחת. פנייתכם תטופל אישית על ידי עו״ד ארז עזרא.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================
   Contact Item ${text}
========================= */

function ContactItem({
  icon: Icon,
  text,
  href,
}: {
  icon: any;
  text: string;
  href: string;
  label: string;
}) {
  const isExternal = href.startsWith("http");

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="flex items-start gap-4 text-slate-300 hover:text-blue-400 transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg p-1 min-w-0"
    >
      <div
        aria-hidden="true"
        className="w-10 h-10 shrink-0 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-blue-500/10 transition-colors"
      >
        <Icon size={18} />
      </div>

      <span
        className={`text-lg font-medium leading-relaxed ${
          href.startsWith("mailto:") ? "break-all" : "break-words"
        }`}
      >
        {text}
      </span>
    </a>
  );
}
